"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

type DialogVariant = "default" | "invitationSheet";

type DialogProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
  variant?: DialogVariant;
};

const Dialog = ({ variant = "default", modal, ...props }: DialogProps) => (
  <DialogPrimitive.Root
    modal={modal ?? (variant === "invitationSheet" ? false : undefined)}
    {...props}
  />
);

const DialogTrigger = DialogPrimitive.Trigger;

type ExtendedDialogPortalProps = DialogPrimitive.DialogPortalProps & {
  className?: string;
  variant?: DialogVariant;
};

export const DialogPortal = ({
  className,
  children,
  variant = "default",
  ...props
}: ExtendedDialogPortalProps) => (
  <DialogPrimitive.Portal {...props}>
    <div
      className={cn(
        "fixed z-50 flex items-start justify-center sm:items-center",
        variant === "invitationSheet" ? "inset-0" : "inset-8",
        className
      )}
    >
      {children}
    </div>
  </DialogPrimitive.Portal>
);
DialogPortal.displayName = DialogPrimitive.Portal.displayName;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed overflow-y-auto grid gap-4 inset-0 z-50 bg-theme-black/40 backdrop-blur-md transition-all duration-100 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in px-5 lg:px-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogComponentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  placement?: "top" | "bottom" | "center";
  variant?: DialogVariant;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogComponentProps
>(({ className, children, placement = "center", variant = "default", ...props }, ref) => {
  const overlayClassName = cn(
    "fixed overflow-y-auto grid gap-4 inset-0 z-50 bg-theme-black/40 backdrop-blur-md transition-all duration-100 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in px-5 lg:px-0",
    {
      "flex items-end justify-center overflow-hidden": placement === "bottom",
      "grid place-items-center": placement !== "bottom",
      "items-start": placement === "top",
      "items-center": placement === "center",
    },
    variant === "invitationSheet" && "px-0 sm:px-5 lg:px-0"
  );

  const content = (
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "w-full gap-4 relative rounded-xl bg-theme-black3 dark:bg-white px-6 py-12 text-white shadow-xl border border-white/10 data-[state=open]:animate-in data-[state=open]:fade-in-90 data-[state=closed]:animate-out sm:rounded-lg",
        placement === "bottom"
          ? "max-h-[calc(100dvh-2rem)] transform-gpu overflow-y-auto duration-300 ease-out will-change-transform data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full data-[state=closed]:duration-200 data-[state=closed]:ease-in"
          : "data-[state=open]:slide-in-from-bottom-10 sm:zoom-in-90 data-[state=open]:sm:slide-in-from-bottom-0",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="x-close absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="w-6 h-6 text-white dark:text-theme-dark" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  );

  if (variant === "invitationSheet") {
    return (
      <DialogPortal variant={variant}>
        <div className={overlayClassName}>{content}</div>
      </DialogPortal>
    );
  }

  return (
    <DialogPortal variant={variant}>
      <DialogOverlay className={overlayClassName}>{content}</DialogOverlay>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-theme-gray", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
